import { Container, Section } from '@repo/ui'
import { WebLayout } from '@/components/layout'

export function TermsPage() {
  return (
    <WebLayout>
      <Section variant="default" className="md:pt-24">
        <Container size="prose">
          <div className="prose prose-neutral dark:prose-invert max-w-none">
            <div className="mb-12">
              <h1 className="text-4xl font-bold tracking-tight mb-2">Terms of Service</h1>
              <p className="text-muted-foreground text-base">Last updated: February 10, 2026</p>
            </div>

            <div className="bg-muted/30 border border-border rounded-lg p-6 mb-12">
              <p className="text-muted-foreground leading-relaxed text-sm m-0">
                These terms govern your use of our service. By creating an account or using our
                platform, you agree to these terms. We've written them to be clear and fair, but
                please read them carefully.
              </p>
            </div>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4">1. Account Terms</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                When you create an account, you're responsible for maintaining its security and for
                all activity that occurs under your account. You must:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Provide accurate and complete information</li>
                <li>Keep your account credentials secure and confidential</li>
                <li>Notify us immediately of any unauthorized access</li>
                <li>Be at least 18 years old or have parental consent</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4">2. Acceptable Use</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We want to maintain a safe and productive environment for everyone. You agree not
                to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Violate any laws or regulations</li>
                <li>Infringe on anyone's intellectual property or other rights</li>
                <li>Send spam, phishing attempts, or malicious content</li>
                <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
                <li>Use our service to harm, harass, or defraud others</li>
                <li>Interfere with or disrupt our service or infrastructure</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4">3. Service and Features</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We continuously improve our service and may:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Add, modify, or remove features at any time</li>
                <li>Change pricing with 30 days notice for existing customers</li>
                <li>Perform scheduled maintenance with reasonable notice</li>
                <li>Implement rate limits or usage restrictions to ensure quality of service</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                We'll do our best to minimize disruption and communicate changes that affect you.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4">4. Payment Terms</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                If you subscribe to a paid plan:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Charges are billed in advance on a recurring basis</li>
                <li>
                  You'll be charged automatically unless you cancel before the next billing cycle
                </li>
                <li>No refunds for partial months or unused features</li>
                <li>Price changes require 30 days advance notice</li>
                <li>Failure to pay may result in service suspension or termination</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4">5. Your Content and Data</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You own the content and data you submit to our service. By using our service, you
                grant us permission to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Store and process your data to provide the service</li>
                <li>Back up your data to prevent loss</li>
                <li>Display your content as necessary to deliver the service</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                You're responsible for maintaining backups of your important data. While we
                implement robust backup systems, you should never rely solely on our service for
                data retention.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4">6. Our Intellectual Property</h2>
              <p className="text-muted-foreground leading-relaxed">
                Our service, including its code, design, features, and branding, is protected by
                intellectual property laws. You may use our service as intended, but you may not
                copy, modify, distribute, or reverse engineer our platform without permission.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4">7. Termination</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You can cancel your account at any time through your account settings. We may
                suspend or terminate your account if you violate these terms or engage in harmful
                behavior.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Upon termination, your access to the service will end. We'll retain your data for 30
                days to allow for account recovery, after which it will be permanently deleted.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4">8. Disclaimers and Limitations</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We work hard to provide reliable service, but we can't guarantee perfection:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>The service is provided "as is" without warranties</li>
                <li>We don't guarantee uninterrupted or error-free operation</li>
                <li>We're not liable for indirect damages or business losses</li>
                <li>
                  Our total liability is limited to the amount you've paid us in the past 12 months
                </li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4">9. Changes to These Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may update these terms occasionally. For material changes, we'll notify you at
                least 30 days in advance via email or through the service. Continued use after
                changes take effect means you accept the new terms.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4">10. Contact</h2>
              <p className="text-muted-foreground leading-relaxed">
                Questions about these terms? Contact us at{' '}
                <a
                  href="mailto:legal@zippity.app"
                  className="text-primary hover:underline font-medium"
                >
                  legal@zippity.app
                </a>
              </p>
            </section>
          </div>
        </Container>
      </Section>
    </WebLayout>
  )
}
